// src/app/features/admin/AdminTradeRequestList.jsx
import React, { useMemo } from "react";
import Button from "../../components/common/Button.jsx";
import useAdminTradeRequests from "./useAdminTradeRequests.js";
import MarketNowBadge from "../../components/admin/MarketNowBadge.jsx";
import { useLivePrices } from "../markets/useLivePrices.js";
import { normalizeSymbol } from "../markets/marketApi.js";
import { ChevronLeft, ChevronRight, Filter, RefreshCw } from "lucide-react";

export default function AdminTradeRequestList() {
  const {
    items,
    total,
    loading,
    error,
    actionBusyId,
    page,
    pageCount,
    prevPage,
    nextPage,
    filters,
    applyFilters,
    resetFilters,
    setPerPage,
    limit,
    approve,
    reject,
  } = useAdminTradeRequests();

  // Collect unique markets on the current page and stream live prices for them
  const pageSymbols = useMemo(() => {
    const uniq = new Set();
    (items || []).forEach((r) => {
      const m = normalizeSymbol(r?.market);
      if (m) uniq.add(m);
    });
    return Array.from(uniq);
  }, [items]);

  const { prices: livePrices, status: liveStatus } = useLivePrices(pageSymbols, {
    enabled: true,
    maxSymbols: 50,
  });

  return (
    <div className="p-4 sm:p-6">
      {/* Filters - Mobile Friendly */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <select
            value={filters.status}
            onChange={(e) => applyFilters({ status: e.target.value })}
            className="px-3 py-2 rounded-lg bg-[#0f0f2a] border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-400/50 transition-all flex-1 sm:flex-none"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <input
            placeholder="Search market..."
            value={filters.market}
            onChange={(e) => applyFilters({ market: e.target.value })}
            className="px-3 py-2 rounded-lg bg-[#0f0f2a] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-indigo-400/50 transition-all flex-1 sm:flex-none sm:w-40"
          />

          <select
            value={filters.direction}
            onChange={(e) => applyFilters({ direction: e.target.value })}
            className="px-3 py-2 rounded-lg bg-[#0f0f2a] border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-400/50 transition-all flex-1 sm:flex-none"
          >
            <option value="">All Directions</option>
            <option value="UP">UP</option>
            <option value="DOWN">DOWN</option>
          </select>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={resetFilters}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-all text-sm flex items-center gap-2"
          >
            <RefreshCw size={14} />
            Reset
          </button>

          <select
            value={String(limit)}
            onChange={(e) => setPerPage(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#0f0f2a] border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-400/50 transition-all"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-4 text-sm">
        <div className="text-white/60">
          <span className="font-semibold text-white">{total}</span> total requests
        </div>

        <div className={`text-xs ${liveStatus.ok ? 'text-emerald-400/60' : 'text-amber-400/60'}`}>
          {liveStatus.msg ? `Live feed: ${liveStatus.msg}` : "● Live feed connected"}
        </div>
      </div>

      {/* Loading & Error States */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-white/40">
            <div className="h-5 w-5 rounded-full border-2 border-indigo-400/30 border-t-indigo-400 animate-spin" />
            <span className="text-sm">Loading requests…</span>
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-rose-400/80 bg-rose-500/10 border border-rose-500/20 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Table - With Horizontal Scroll for Mobile */}
      {!loading && !error && (
        <div className="rounded-xl bg-[#0f0f2a]/50 backdrop-blur-sm border border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 bg-white/5">
                  <th className="text-left px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Created
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Market
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Direction
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Market Now
                  </th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Stake
                  </th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Duration
                  </th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Payout
                  </th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/5">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-white/30">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-4xl mb-2">📭</div>
                        <p className="text-sm">No trade requests found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((r) => {
                    const sym = normalizeSymbol(r?.market);
                    const live = sym ? livePrices?.[sym] : null;

                    const isPending = r.status === "PENDING";
                    const isBusy = actionBusyId === r.id;

                    return (
                      <tr key={r.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-3 py-3 text-white/60 whitespace-nowrap text-xs">
                          {r.createdAt ? new Date(r.createdAt).toLocaleString() : "-"}
                        </td>

                        <td className="px-3 py-3 text-white font-medium whitespace-nowrap">
                          {r.market}
                        </td>

                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            r.direction === "UP" 
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20" 
                              : "bg-rose-500/20 text-rose-400 border border-rose-500/20"
                          }`}>
                            {r.direction}
                          </span>
                        </td>

                        <td className="px-3 py-3 whitespace-nowrap">
                          <MarketNowBadge 
                            current={live?.price ?? null} 
                            previous={live?.prevPrice ?? null} 
                          />
                          <div className="text-xs text-white/30 mt-1">
                            {Number.isFinite(Number(live?.price))
                              ? `$${Number(live.price).toFixed(4)}`
                              : "waiting…"}
                          </div>
                        </td>

                        <td className="px-3 py-3 text-right text-white font-mono whitespace-nowrap">
                          {r.stakeAmount}
                        </td>

                        <td className="px-3 py-3 text-right text-white/60 whitespace-nowrap">
                          {r.durationSeconds}s
                        </td>

                        <td className="px-3 py-3 text-right text-white/60 whitespace-nowrap">
                          {r.payoutMultiplier}x
                        </td>

                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            r.status === "PENDING" 
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/20"
                              : r.status === "APPROVED"
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                              : "bg-rose-500/20 text-rose-400 border border-rose-500/20"
                          }`}>
                            {r.status}
                          </span>
                        </td>

                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => approve(r.id)}
                              disabled={!isPending || isBusy}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                isPending && !isBusy
                                  ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/20"
                                  : "bg-white/5 text-white/20 cursor-not-allowed"
                              }`}
                            >
                              Approve
                            </button>

                            <button
                              onClick={() => reject(r.id)}
                              disabled={!isPending || isBusy}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                isPending && !isBusy
                                  ? "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 border border-rose-500/20"
                                  : "bg-white/5 text-white/20 cursor-not-allowed"
                              }`}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
          <div className="text-xs text-white/30">
            Page {page} of {pageCount}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={prevPage}
              disabled={page <= 1}
              className="flex items-center gap-1 px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            <button
              onClick={nextPage}
              disabled={page >= pageCount}
              className="flex items-center gap-1 px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
